import Immutable, { List, OrderedMap } from 'immutable';
import { Markup, MarkupMap } from 'records/Markup';
import { RFI, RFIOrderedMap } from 'records/RFI';
import { RFIQuery, SortDirection } from 'records/RFIQuery';
import { RoleMap } from 'records/Role';
import { SheetMap } from 'records/Sheet';
import { SheetVersion } from 'records/SheetVersion';
import { User } from 'records/User';
import { getRfiMarkupSheets, sortRfisFromGroups } from 'utils/rfi';

export function filterOverdue(rfi: RFI, rfiQuery: RFIQuery): boolean {
  if (rfiQuery.overdue.size > 0) {
    const comp = rfiQuery.overdue.first<number>();
    const dueDate = new Date(rfi.duedate * 1000);
    if (rfi.duedate === 0) {
      return false;
    }
    return dueDate < new Date(comp) && rfi.answer === null;
  }

  return true;
}

export function filterActive(rfi: RFI): boolean {
  return rfi.answer === null;
}

export function filterOpen(rfi: RFI, rfiQuery: RFIQuery): boolean {
  if (rfiQuery.open) {
    return rfi.answer === null;
  }

  return true;
}

export function filterClosed(rfi: RFI, rfiQuery: RFIQuery): boolean {
  if (rfiQuery.closed) {
    return rfi.answer !== null;
  }

  return true;
}

export function filterDrafts(rfi: RFI, rfiQuery: RFIQuery): boolean {
  if (rfiQuery.drafts) {
    return rfi.number === -1;
  }

  return true;
}

export function filterAssignees(rfi: RFI, rfiQuery: RFIQuery): boolean {
  if (rfiQuery.assignees.size === 0) {
    return true;
  }

  if (
    rfi.assignees.some((a) => rfiQuery.assignees.some((qa) => qa.id === a.id))
  ) {
    return true;
  }

  return false;
}

export function filterSheets(
  rfi: RFI,
  markups: Immutable.OrderedMap<string, MarkupMap>,
  rfiQuery: RFIQuery
): boolean {
  const { pages } = rfiQuery;

  // if pages is null, that means don't use it as a filter criteria
  if (pages === null) return true;

  // if pages is a Set, but empty, then that means to filter by sheets, but none are given.
  if (pages.size === 0) return false;

  return markups
    .filter((_, k) => pages.has(k)) // filter for markups that only exist on queried pages.
    .reduce<MarkupMap>((acc, v) => acc.merge(v), Immutable.OrderedMap()) // Make one big map of all remaining markups.
    .some((mm) => rfi.postedmarkupids.includes(mm.markupid)); // see if any of the sheet markups are related to the tested rfi.
}

export function filterRFIs(
  rfis: RFIOrderedMap,
  roles: RoleMap,
  markups: Immutable.OrderedMap<string, MarkupMap>,
  rfiQuery: RFIQuery
): RFIOrderedMap {
  return rfis
    .filter((rfi) => filterSheets(rfi, markups, rfiQuery))
    .filter((rfi) => filterAssignees(rfi, rfiQuery))
    .filter((rfi) => filterOverdue(rfi, rfiQuery))
    .filter((rfi) => filterOpen(rfi, rfiQuery))
    .filter((rfi) => filterClosed(rfi, rfiQuery))
    .filter((rfi) => filterDrafts(rfi, rfiQuery));
}

interface SortHandlers {
  [key: string]: (
    rfis: RFIOrderedMap,
    direction: SortDirection,
    sheets: SheetMap
  ) => RFIOrderedMap;
}

const sortHandlers: SortHandlers = {
  number: (rfis: RFIOrderedMap, direction: SortDirection): RFIOrderedMap => {
    const sortedRFIs = rfis.sort((rfiA, rfiB): number => {
      const a = direction === 'ascending' ? rfiA : rfiB;
      const b = direction === 'ascending' ? rfiB : rfiA;
      if (a.number > b.number) {
        return 1;
      }
      if (a.number < b.number) {
        return -1;
      }
      if (a.number === b.number) {
        if (a.versionnumber > b.versionnumber) {
          return -1;
        }
        if (a.versionnumber < b.versionnumber) {
          return 1;
        }
      }

      return 0;
    });

    return sortedRFIs;
  },

  title: (rfis: RFIOrderedMap, direction: SortDirection): RFIOrderedMap => {
    const sortedRFIs = rfis.sort((rfiA, rfiB): number => {
      const a =
        direction === 'ascending'
          ? rfiA.title.toLowerCase()
          : rfiB.title.toLowerCase();
      const b =
        direction === 'ascending'
          ? rfiB.title.toLowerCase()
          : rfiA.title.toLowerCase();

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedRFIs;
  },

  craeteddate: (
    rfis: RFIOrderedMap,
    direction: SortDirection
  ): RFIOrderedMap => {
    const groupedRFIs = groupRFIs(rfis);
    const sortedRFIs = groupedRFIs.sort((rfiA, rfiB) => {
      const dateA = rfiA.first<RFI>().craeteddate || 0;
      const dateB = rfiB.first<RFI>().craeteddate || 0;

      const a = direction === 'ascending' ? dateA : dateB;
      const b = direction === 'ascending' ? dateB : dateA;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    const updatedRFIList = sortRfisFromGroups(sortedRFIs);
    return updatedRFIList;
  },
  duedate: (rfis: RFIOrderedMap, direction: SortDirection): RFIOrderedMap => {
    const groupedRFIs = groupRFIs(rfis);
    const sortedRFIs = groupedRFIs.sort((rfiA, rfiB) => {
      const dateA = rfiA.first<RFI>().duedate || 0;
      const dateB = rfiB.first<RFI>().duedate || 0;

      const a = direction === 'ascending' ? dateA : dateB;
      const b = direction === 'ascending' ? dateB : dateA;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    const updatedRFIList = sortRfisFromGroups(sortedRFIs);
    return updatedRFIList;
  },

  status: (rfis: RFIOrderedMap, direction: SortDirection): RFIOrderedMap => {
    const groupedRFIs = groupRFIs(rfis);
    const sortedRFIs = groupedRFIs.sort((rfiA, rfiB) => {
      const rfiAHasAnswer = rfiA.first<RFI>().answer !== null;
      const rfiBHasAnswer = rfiB.first<RFI>().answer !== null;

      const a = direction === 'ascending' ? rfiAHasAnswer : rfiBHasAnswer;
      const b = direction === 'ascending' ? rfiBHasAnswer : rfiAHasAnswer;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    const updatedRFIList = sortRfisFromGroups(sortedRFIs);
    return updatedRFIList;
  },

  assignee: (rfis: RFIOrderedMap, direction: SortDirection): RFIOrderedMap => {
    const groupedRFIs = groupRFIs(rfis);
    const sortedRFIs = groupedRFIs.sort((rfiA, rfiB) => {
      const emailA =
        rfiA.first<RFI>().assignees.first<User>().email.toLowerCase() || '';
      const emailB =
        rfiB.first<RFI>().assignees.first<User>().email.toLowerCase() || '';

      const a = direction === 'ascending' ? emailA : emailB;
      const b = direction === 'ascending' ? emailB : emailA;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    const updatedRFIList = sortRfisFromGroups(sortedRFIs);
    return updatedRFIList;
  },

  sheets: (
    rfis: RFIOrderedMap,
    direction: SortDirection,
    sheets: SheetMap
  ): RFIOrderedMap => {
    const groupedRFIs = groupRFIs(rfis);
    const sortedRFIs = groupedRFIs.sort((rfiA, rfiB) => {
      const sheetListA =
        getRfiMarkupSheets(rfiA.first<RFI>().postedmarkups, sheets) ||
        List<SheetVersion>();
      const sheetListB =
        getRfiMarkupSheets(rfiB.first<RFI>().postedmarkups, sheets) ||
        List<SheetVersion>();

      const sheetA =
        sheetListA !== undefined && sheetListA.size > 0
          ? sheetListA.first<SheetVersion>().sheetnumber.toLowerCase()
          : '';
      const sheetB =
        sheetListB !== undefined && sheetListB.size > 0
          ? sheetListB.first<SheetVersion>().sheetnumber.toLowerCase()
          : '';

      const a = direction === 'ascending' ? sheetA : sheetB;
      const b = direction === 'ascending' ? sheetB : sheetA;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    const updatedRFIList = sortRfisFromGroups(sortedRFIs);
    return updatedRFIList;
  },
};

export function sortRFIs(
  unsortedRFIs: RFIOrderedMap,
  sheets: SheetMap,
  rfiQuery: RFIQuery
): RFIOrderedMap {
  const { sortField, sortDirection } = rfiQuery;

  const groups = unsortedRFIs.groupBy((rfi) => rfi.number > 0);

  const drafts = groups
    .get(false, Immutable.OrderedMap<string, RFI>())
    .toOrderedMap();

  let rfis = groups
    .get(true, Immutable.OrderedMap<string, RFI>())
    .toOrderedMap();

  if (sortHandlers[sortField]) {
    rfis = sortHandlers[sortField](rfis, sortDirection, sheets);
  }

  return drafts.concat(rfis);
}

export function searchRFIs(
  rfis: RFIOrderedMap,
  rfiQuery: RFIQuery
): RFIOrderedMap {
  const { search } = rfiQuery;

  if (search.length === 0) {
    return rfis;
  }

  const sanitizedSearch = search.toLowerCase().replaceAll(/[.-\s]+/g, '');

  const results = rfis.filter((rfi) => {
    if (rfi.number.toString().indexOf(sanitizedSearch) === 0) {
      return true;
    }

    const sanitizedTitle = rfi.title.toLowerCase().replaceAll(/[.-\s]+/g, '');

    return sanitizedTitle.includes(sanitizedSearch);
  });

  return results;
}

export default function queryRFIs(
  rfis: RFIOrderedMap,
  roles: RoleMap,
  markups: Immutable.OrderedMap<string, MarkupMap>,
  sheets: SheetMap,
  rfiQuery: RFIQuery
): RFIOrderedMap {
  const filteredRFIs = filterRFIs(rfis, roles, markups, rfiQuery);

  const searchedRFIs = searchRFIs(filteredRFIs, rfiQuery);

  const sortedRFIs = sortRFIs(searchedRFIs, sheets, rfiQuery);

  return sortedRFIs;
}

export function groupRFIs(rfis: RFIOrderedMap): OrderedMap<string, List<RFI>> {
  const groupedRFIs = OrderedMap(
    rfis.reduce((result: OrderedMap<string, List<RFI>>, item: RFI) => {
      const itemId = item.id.split('+')[0];
      const groupedRFIs = result.get(itemId) || List<RFI>();
      const updatedGroup = groupedRFIs
        .push(item)
        .sort((a, b) => b.versionnumber - a.versionnumber);
      return result.setIn([itemId], updatedGroup);
    }, OrderedMap<string, List<RFI>>())
  );

  return groupedRFIs;
}

interface RFIMarkup {
  rfi: RFI;
  markup: Markup;
}

export function queryRFIMarkups(
  rfis: RFIOrderedMap,
  roles: RoleMap,
  markups: Immutable.OrderedMap<string, MarkupMap>,
  sheets: SheetMap,
  rfiQuery: RFIQuery
): Immutable.List<RFIMarkup> {
  const queriedRFIs = queryRFIs(rfis, roles, markups, sheets, rfiQuery);

  const flattenedMarkups = markups.reduce(
    (acc, v) => acc.merge(v),
    Immutable.Map<string, Markup>()
  );

  const rfiMarkups = queriedRFIs
    .valueSeq()
    .flatMap((rfi) => {
      return rfi.postedmarkupids.map((pmid) => {
        const markup = flattenedMarkups.get(pmid, new Markup());

        return {
          rfi,
          markup,
        };
      });
    })
    .filter((rfim) => rfim.markup.markupid !== '')
    .toList();

  return rfiMarkups;
}

export function getMarkupsFromRFIIDs(
  rfis: RFIOrderedMap,
  markups: Immutable.Map<string, MarkupMap>,
  rfiids: Immutable.List<string>
): Immutable.List<RFIMarkup> {
  const flattenedMarkups = markups.reduce(
    (acc, v) => acc.merge(v),
    Immutable.Map<string, Markup>()
  );

  const qRFIs = rfiids
    .map((rfiid) => rfis.get(rfiid, new RFI()))
    .filter((rfi) => rfi.id !== '');

  const rfiMarkups = qRFIs
    .flatMap((rfi) => {
      return rfi.postedmarkupids.map((pmid) => {
        const markup = flattenedMarkups.get(pmid, new Markup());

        return {
          rfi,
          markup,
        };
      });
    })
    .filter((rfim) => rfim.markup.markupid !== '');

  return rfiMarkups;
}
